import type { Editor } from '@tiptap/react';

import { cn } from '@repo/lib/cn';
import { useEditorState } from '@tiptap/react';
import { Heading1Icon, Heading2Icon, Heading3Icon, Heading4Icon, Heading5Icon, Heading6Icon, ListIcon } from 'lucide-react';
import { useCallback, useMemo } from 'react';

import type { DropdownOption } from '@/components/ui/tiptap/tiptap-ui-primitives/dropdown-menu';

import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { useTiptapEditor } from '@/hooks/tiptap';
import { isNodeInSchema, shouldShow } from '@/lib/tiptap';

import type { HeadingButtonProps, Level } from '.';

export const headingOptions: DropdownOption<Level>[] = [
  {
    type: 1,
    icon: Heading1Icon,
    label: 'Nagłówek 1',
  },
  {
    type: 2,
    icon: Heading2Icon,
    label: 'Nagłówek 2',
  },
  {
    type: 3,
    icon: Heading3Icon,
    label: 'Nagłówek 3',
  },
  {
    type: 4,
    icon: Heading4Icon,
    label: 'Nagłówek 4',
  },
  {
    type: 5,
    icon: Heading5Icon,
    label: 'Nagłówek 5',
  },
  {
    type: 6,
    icon: Heading6Icon,
    label: 'Nagłówek 6',
  },
];

export const headingShortcutKeys: Record<Level, string> = {
  1: 'Ctrl-Alt-1',
  2: 'Ctrl-Alt-2',
  3: 'Ctrl-Alt-3',
  4: 'Ctrl-Alt-4',
  5: 'Ctrl-Alt-5',
  6: 'Ctrl-Alt-6',
};

export function useIsActiveElement(editor: Editor | null, level: Level): boolean {
  const isActive = useEditorState({
    editor,
    selector: (ctx) => {
      return ctx.editor?.isActive('heading', { level: level }) ?? false;
    },
  });

  return isActive ?? false;
}

function toggleHeading(editor: Editor | null, level: Level): void {
  if (!editor) return;

  if (editor.isActive('heading', { level })) {
    editor.chain().focus().setNode('paragraph').run();
  } else {
    editor.chain().focus().toggleNode('heading', 'paragraph', { level }).run();
  }
}

function useIsHeadingButtonDisabled(editor: Editor | null, level: Level, userDisabled: boolean = false): boolean {
  const isDisabled = useEditorState({
    editor,
    selector: (ctx) => {
      return !ctx.editor?.can().toggleNode('heading', 'paragraph', { level });
    },
  });

  if (!editor) return true;
  if (userDisabled) return true;
  if (isDisabled) return true;
  return false;
}

function getOption(type: Level): DropdownOption<Level> | undefined {
  return headingOptions.find((option) => option.type === type);
}

function useHeadingButtonState(editor: Editor | null, level: Level, disabled: boolean = false) {
  const inSchema: boolean = isNodeInSchema('heading', editor);
  const option: DropdownOption<Level> | undefined = getOption(level);
  const isDisabled: boolean = useIsHeadingButtonDisabled(editor, level, disabled);
  const isActive: boolean = useIsActiveElement(editor, level);
  const shortcutKey: string | undefined = headingShortcutKeys[level];

  return {
    inSchema,
    option,
    isActive,
    isDisabled,
    shortcutKey,
  };
}

function useHandleClick(onClick: HeadingButtonProps['onClick'], callback: () => void) {
  return useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      onClick?.(e);

      if (!e.defaultPrevented) {
        callback();
      }
    },
    [onClick, callback]
  );
}

export function HeadingButton(headingButtonProps: HeadingButtonProps) {
  const { editor: providedEditor, level, disabled, onClick, children, className, ...props } = headingButtonProps;

  const editor: Editor | null = useTiptapEditor(providedEditor);
  const { inSchema, isDisabled, isActive, option } = useHeadingButtonState(editor, level, disabled);

  const Icon = option?.icon || ListIcon;

  const handleClick = useHandleClick(onClick, () => {
    toggleHeading(editor, level);
  });

  const show: boolean = useMemo(() => {
    return shouldShow([!!editor, inSchema]);
  }, [editor, inSchema]);

  if (!show || !editor || !editor.isEditable) {
    return null;
  }

  return (
    <DropdownMenuItem
      data-active-state={isActive ? 'on' : 'off'}
      disabled={isDisabled}
      onClick={handleClick}
      aria-label={option?.label || String(level)}
      aria-pressed={isActive}
      className={cn('group aria-pressed:text-text-primary aria-pressed:bg-fill-secondary/60 aria-pressed:hover:bg-fill-secondary/80', className)}
      {...props}
    >
      {children || (
        <>
          <Icon className="group:aria-pressed:text-text-primary" />
          {option?.label || String(level)}
        </>
      )}
    </DropdownMenuItem>
  );
}
